import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { TableHeader } from 'components/table/table-content';
import { PaginationHandler } from 'elements/pagination-handler';
import {
  CertificateProducer,
  MooringCertificateRecipientType,
  MooringArticleCategory,
  MooringArticleType,
  MooringCertificate,
  MooringCertificateListItem,
  MooringProductType,
  SendMooringCertificates,
} from 'models/mooring';
import {
  ErpSystemService,
  MooringArticleCategoryService,
  MooringArticleTypeService,
  MooringCertificateService,
  MooringProductTypeService,
} from 'services';
import { ErrorService } from 'services/error-service';
import { ToastService } from 'services/toast-service';
import { Router } from 'aurelia-router';
import { DataFilter } from 'lib/tables/DataFilter';
import { PubSub } from 'lib/event/PubSub';
import { Filters, filterTypes } from 'elements/Filter';
import { DialogService } from 'aurelia-dialog';
import { CertificateDialog } from 'views/mooring/mooring-certificate/certificate-dialog';
import { TabManager } from 'views/admin/mooring/tab-manager';
import { CustomerService } from 'services/customer-service';
import { SiteService } from 'services/site-service';
import { SystemFileStorageService } from 'services/system-file-storage-service';
import { Models } from 'models/core';
import { FilterManager } from 'lib/tables/FilterManager';
import { ErpSystem } from 'models/ErpSystem';
import { CertificateSendType } from 'components/dialogs/send-certificates-choice/send-certificates-choice-dialog';
import {
  MooringCertificateBulkActionDialog,
  MooringCertificateBulkActionDialogOutput,
  MooringCertificateBulkActionType,
} from './mooring-certificate-bulk-action-dialog';

type TableFilter = {
  customerIds?: string[];
  localityIds?: string[];
  dateOfGoodsReceiptFrom?: string;
  dateOfGoodsReceiptTo?: string;
  categoryIds?: string[];
  typeIds?: string[];
  productIds?: string[];
} & DataFilter;

type TableCertificate = {
  id: number;
  type: 'system';
};

@autoinject()
export class TabMooringComponentCertificate {
  protected certificates: (MooringCertificateListItem & { _certificates: TableCertificate[]; isChecked?: boolean })[] = [];
  protected selectedCertificatesCount = 0;
  private selectedCertificates = new Map<number, TableCertificate[]>();
  protected dataFilter: TableFilter = new DataFilter();

  protected customers: Models.Customer[] = [];
  protected localitys: Models.Site[] = [];
  protected mooringArticleCategories: MooringArticleCategory[] = [];
  protected mooringArticleTypes: MooringArticleType[] = [];
  protected mooringArticleProductTypes: MooringProductType[] = [];
  protected erpSystems: ErpSystem[] = [];

  protected canCheck: boolean = false;
  protected downloadProgress = false;
  protected sendEmailProgress = false;

  protected tableHeaders: TableHeader<MooringCertificateListItem & { actions: ''; IsChecked: boolean }>[] = [
    { key: 'IsChecked', label: '', sortable: false },
    { key: 'ApprovedAt', label: '', sortable: false },
    { key: 'Id', label: 'general.id', sortable: true },

    { key: 'SupplierName', label: 'general.supplier', sortable: true },
    { key: 'CertificateProducer', label: 'general.certificate', sortable: true },
    { key: 'CategoryName', label: 'general.category', sortable: true },
    { key: 'TypeName', label: 'general.type', sortable: true },
    { key: 'ProductTypeName', label: 'general.product', sortable: true },

    { key: 'ErpSystemName', label: 'general.erpSystem', sortable: false },
    { key: 'ArticleNo', label: 'general.articlenr', sortable: true },
    { key: 'ProductName', label: 'general.productName', sortable: true },

    { key: 'BatchNumber', label: 'general.batchNumber', sortable: true },
    { key: 'BatchNumberTo', label: 'general.batchNumber', sortable: true },

    { key: 'DateOfGoodsReceipt', label: 'general.goodsReceipt', sortable: true },
    { key: 'Quantity', label: 'general.quantity', sortable: true },

    { key: 'actions', label: '', sortable: false },
  ];

  constructor(
    private errorService: ErrorService,
    private certificateService: MooringCertificateService,
    private toast: ToastService,
    private router: Router,
    private dialogService: DialogService,
    private pubsub: PubSub,
    private t: I18N,
    private tabManager: TabManager,
    private customerService: CustomerService,
    private siteService: SiteService,
    private mooringArticleCategoryService: MooringArticleCategoryService,
    private mooringArticleTypeService: MooringArticleTypeService,
    private mooringProductTypeService: MooringProductTypeService,
    private systemFilesService: SystemFileStorageService,
    protected fm: FilterManager,
    private erpSystemService: ErpSystemService
  ) {
    tabManager.tabActivated();
    this.pubsub.sub('filter-manager:filter-changed', () => {
      void this.getCertificates();
    });

    this.pubsub.sub('list-entity:updated', () => {
      void this.getCertificates();
    });

    this.pubsub.sub('list-entity:created', () => {
      void this.getCertificates();
    });

    //   this.pubsub.sub('export-list', (data) => {
    //     if (data === 'mooring-certificates') {
    //       void this.getCertificates({ export: true });
    //     }
    //   });
  }

  protected getCertificateProducer(certificateProducer: CertificateProducer) {
    const p = +certificateProducer;
    if (isNaN(p) || !Number.isInteger(p)) return '';
    if (certificateProducer === CertificateProducer.MORENOT) return 'Mørenot';
    if (certificateProducer === CertificateProducer.SUPPLIER) return this.t.tr('general.supplier');
  }

  protected async onDelete(id: number) {
    try {
      if (id) {
        this.toast.showSuccess(this.t.tr('general.deleted'));
        await this.getCertificates();
      }
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected async onSave(certificate: MooringCertificate) {
    try {
      if (certificate.Id) {
        await this.certificateService.put(certificate, certificate.Id);
      } else {
        await this.certificateService.post(certificate);
      }
      this.toast.showSuccess(this.t.tr('general.saved'));
      await this.getCertificates();
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected editCertificate(_: number, id: number) {
    void this.dialogService
      .open({
        viewModel: CertificateDialog,
        model: {
          certificateId: id,
        },
        position: () => ({ top: 20 }),
      })
      .whenClosed(() => {
        document.querySelector('html').style.overflowY = null;
        void this.getCertificates();
      });
  }

  async attached() {
    await this.load();

    this.fm.init({
      context: 'mooring_certificates',
      storageKey: 'mooring_certificates',
      filterFilters: (filters) => {
        const categoryIds = Object.entries(filters.categoryIds.values)
          .filter(([, value]) => value)
          .map(([key]) => +key);

        filters.typeIds.options = this.mooringArticleTypes
          .filter((x) => categoryIds.includes(x.CategoryId))
          .map((x) => ({ Name: x.Name, Id: x.Id }));

        if (categoryIds.length === 0) {
          this.fm.resetFilter('typeIds');
          this.fm.resetFilter('productIds');
        }

        const productIds = Object.entries(filters.typeIds.values)
          .filter(([, value]) => value)
          .map(([key]) => +key);

        filters.productIds.options = this.mooringArticleProductTypes
          .filter((x) => productIds.includes(x.ArticleTypeId))
          .map((x) => ({ Name: x.Name, Id: x.Id }));

        if (productIds.length === 0) {
          this.fm.resetFilter('productIds');
        }

        return filters;
      },
      filters: {
        customerIds: {
          name: 'customerIds',
          label: this.t.tr('general.customer'),
          type: filterTypes.CHECKBOX,
          options: this.customers,
          query: 'customerIds',
        },
        localityIds: {
          name: 'localityIds',
          label: this.t.tr('general.locality'),
          type: filterTypes.CHECKBOX,
          options: this.localitys,
          query: 'localityIds',
        },
        dateOfGoodsReceipt: {
          name: 'dateOfGoodsReceipt',
          label: this.t.tr('general.goodsReceipt'),
          type: filterTypes.RANGE_DATE,
        },
        categoryIds: {
          name: 'categoryIds',
          label: this.t.tr('general.category'),
          type: filterTypes.CHECKBOX,
          originalData: this.mooringArticleCategories,
          options: this.mooringArticleCategories.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'categoryIds',
          values: {},
        },
        typeIds: {
          name: 'typeIds',
          label: this.t.tr('general.type'),
          type: filterTypes.CHECKBOX,
          originalData: this.mooringArticleTypes,
          options: this.mooringArticleTypes.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'typeIds',
          values: {},
        },
        productIds: {
          name: 'productIds',
          label: this.t.tr('general.product'),
          type: filterTypes.CHECKBOX,
          originalData: this.mooringArticleProductTypes,
          options: this.mooringArticleProductTypes.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'productIds',
          values: {},
        },
        erpIds: {
          name: 'erpIds',
          label: this.t.tr('general.erpSystem'),
          type: filterTypes.CHECKBOX,
          originalData: this.erpSystems,
          options: this.erpSystems.map((x) => ({
            Name: x.Title,
            Id: x.Id,
          })),
          query: 'erpIds',
        },
      } satisfies Filters,
      defaultFilters: {
        customerIds: true,
        localityIds: true,
        dateOfGoodsReceipt: true,
        categoryIds: true,
        typeIds: true,
        productIds: true,
        erpIds: true,
      },
    });

    await this.getCertificates();

    const certificateId = this.router.currentInstruction.queryParams['Id'];
    if (certificateId) {
      setTimeout(() => {
        this.editCertificate(null, +certificateId);
      }, 200);
    }
  }

  protected detached() {
    this.pubsub.unsub();
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  protected downloadCertificate(attachmentId: number) {
    try {
      void this.systemFilesService.download(attachmentId, true);
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  private async load() {
    const [customers, localitys, mooringArticleCategories, mooringArticleTypes, mooringProductTypes, erpSystems] = await Promise.all([
      this.customerService.getAll(),
      this.siteService.getAll(),
      this.mooringArticleCategoryService.getAll(),
      this.mooringArticleTypeService.getAll(),
      this.mooringProductTypeService.getAll(),
      this.erpSystemService.getAll(),
    ]);

    this.customers = customers;
    this.localitys = localitys;
    this.mooringArticleCategories = mooringArticleCategories;
    this.mooringArticleTypes = mooringArticleTypes;
    this.mooringArticleProductTypes = mooringProductTypes;
    this.erpSystems = erpSystems;
  }

  protected async getCertificates() {
    try {
      const response = await this.certificateService.table(this.fm.queryFilter);
      if (!response) return;
      this.fm.paginationTotalRecords = +response.headers.get('x-total-count') || 0;
      this.certificates = response.data.map((x) => ({
        ...x,
        _certificates: x.Certificates.map((c) => ({ id: c.AttachmentId, type: 'system' as const })),
        isChecked: this.selectedCertificates.has(x.Id),
      }));

      this.updateSelectedCertificatesCount();
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected setCanCheck() {
    if (!this.canCheck) {
      this.certificates.forEach((certificate) => {
        certificate.isChecked = false;
      });
      this.selectedCertificates.clear();
    }

    this.canCheck = !this.canCheck;
    if (this.canCheck === false) {
      this.selectedCertificatesCount = 0;
    }
  }

  protected onCertificateChecked(row: MooringCertificateListItem & { _certificates: TableCertificate[]; isChecked?: boolean }) {
    if (row.isChecked) {
      this.selectedCertificates.set(row.Id, row._certificates);
    } else {
      this.selectedCertificates.delete(row.Id);
    }

    this.updateSelectedCertificatesCount();
  }

  protected async onDownloadSelectedCertificates() {
    if (!this.selectedCertificatesCount) {
      this.toast.showWarning(this.t.tr('general.selectAtLeastOneCertificate'));
      return;
    }

    const dialog = this.dialogService.open({
      viewModel: MooringCertificateBulkActionDialog,
      model: {
        action: MooringCertificateBulkActionType.Download,
        selectedCount: this.selectedCertificatesCount,
      },
    });

    const result = await dialog.whenClosed();
    if (result.wasCancelled) {
      return;
    }

    const output = result.output as MooringCertificateBulkActionDialogOutput;
    if (output.sendType === CertificateSendType.Zip) {
      await this.downloadSelectedCertificatesAsZip();
      this.setCanCheck();
      return;
    }

    await this.downloadSelectedCertificatesAsSeparateFiles();
    this.setCanCheck();
  }

  protected async onSendSelectedCertificatesByEmail() {
    if (!this.selectedCertificatesCount) {
      this.toast.showWarning(this.t.tr('general.selectAtLeastOneCertificate'));
      return;
    }

    const dialog = this.dialogService.open({
      viewModel: MooringCertificateBulkActionDialog,
      model: {
        action: MooringCertificateBulkActionType.Email,
        selectedCount: this.selectedCertificatesCount,
      },
    });

    const result = await dialog.whenClosed();
    if (result.wasCancelled) {
      return;
    }

    const output = result.output as MooringCertificateBulkActionDialogOutput;
    const request = new SendMooringCertificates();
    request.CertificateIds = this.selectedCertificateIds;
    request.SendType = output.sendType;
    request.RecipientType = output.recipientType ?? MooringCertificateRecipientType.Me;
    request.RecipientEmail = output.recipientEmail;

    try {
      this.sendEmailProgress = true;
      await this.certificateService.sendSelectedCertificates(request);
      this.toast.showSuccess('general.certificatesSent');
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.sendEmailProgress = false;
      this.setCanCheck();
    }
  }

  private get selectedCertificateIds() {
    return Array.from(this.selectedCertificates.keys());
  }

  private updateSelectedCertificatesCount() {
    this.selectedCertificatesCount = this.selectedCertificates.size;
  }

  private async downloadSelectedCertificatesAsZip() {
    try {
      this.downloadProgress = true;
      const { blob, fileName } = await this.certificateService.downloadSelectedCertificatesZip(this.selectedCertificateIds);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.downloadProgress = false;
    }
  }

  private async downloadSelectedCertificatesAsSeparateFiles() {
    const fileIds = [...new Set(Array.from(this.selectedCertificates.values()).flatMap((files) => files.map((file) => file.id)))];

    try {
      this.downloadProgress = true;
      for (const fileId of fileIds) {
        await this.systemFilesService.download(fileId, true);
      }
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.downloadProgress = false;
    }
  }

  // Aurelia lifecycle
  protected canDeactivate() {
    return this.tabManager.leaveTab();
  }
}
